import { useMemo } from 'react';

import { getEntityTypeLabelKey, MAX_RESULTS_PER_GROUP } from '@/features/header/consts/search';
import { useSearchContext } from '@/features/header/context/SearchContext';
import { useRouteModuleCode } from '@/features/header/hooks/useRouteModuleCode';
import type { EntitySearchRow, SearchSection } from '@/features/header/types/searchResults';
import { useGlobalSearchQuery, type GlobalSearchResult } from '@/modules/PMS/api/global-search';

interface UseGlobalSearchResultsReturn {
  sections: SearchSection[];
  isFetching: boolean;
  isError: boolean;
}

const toRouterPath = (navigationTarget: string): string =>
  navigationTarget.startsWith('/') ? navigationTarget : `/${navigationTarget}`;

const groupByEntityType = (results: GlobalSearchResult[]): SearchSection[] => {
  const sectionsByType = new Map<string, SearchSection>();
  const order: string[] = [];

  for (const result of results) {
    let section = sectionsByType.get(result.EntityType);
    if (!section) {
      section = {
        id: result.EntityType,
        labelKey: getEntityTypeLabelKey(result.EntityType),
        fallbackLabel: result.EntityType,
        rows: []
      };
      sectionsByType.set(result.EntityType, section);
      order.push(result.EntityType);
    }

    if (section.rows.length >= MAX_RESULTS_PER_GROUP) {
      continue;
    }

    const row: EntitySearchRow = {
      kind: 'entity',
      entityType: result.EntityType,
      id: result.Id,
      label: result.Label,
      breadcrumb: result.Breadcrumb,
      navigationTarget: toRouterPath(result.NavigationTarget)
    };
    section.rows.push(row);
  }

  return order.flatMap((type) => {
    const section = sectionsByType.get(type);
    return section ? [section] : [];
  });
};

export const useGlobalSearchResults = (query: string): UseGlobalSearchResultsReturn => {
  const { entityType, vesselId } = useSearchContext();
  const moduleCode = useRouteModuleCode();

  const request = useMemo(
    () => ({
      Query: query.trim(),
      CurrentModuleCode: moduleCode,
      CurrentEntityType: entityType ?? '',
      CurrentVesselId: vesselId
    }),
    [query, moduleCode, entityType, vesselId]
  );

  const { data, isFetching, isError } = useGlobalSearchQuery(request, true);

  const sections = useMemo(() => (data ? groupByEntityType(data.Results) : []), [data]);

  return { sections, isFetching, isError };
};
